import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Icon, Input, OtpInput, PasswordInput, Text, Title } from "@app/ui";
import { useAuth } from "../auth/AuthContext.js";
import { AuthControls } from "../auth/AuthShell.js";
import { PasswordChecklist, passwordMeetsPolicy } from "../auth/PasswordChecklist.js";
import { ErrorAlert } from "../errors/index.js";
import { useLang, useT } from "../i18n.js";
import { PassportCard3D } from "../landing/PassportCard3D.js";
import { BrandMark } from "../components/BrandMark.js";
import "../landing/landing.css";

type Mode = "login" | "register";
/** The auth flow is a tiny state machine; each step owns its title, form and focus. */
type Step = "credentials" | "mfa" | "expired";

export function LoginPage() {
  const { login, register, verifyMfa, completeExpiredPassword, user } = useAuth();
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();

  const [mode, setMode] = useState<Mode>("login");
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  /** Short-lived server token carried between the credentials step and mfa/expired. */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [pwdRejected, setPwdRejected] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the visitor came from an invite link — reassure them the invite resumes.
  const [pendingInvite] = useState(() => !!sessionStorage.getItem("pendingInviteToken"));

  // After auth, resume a pending invite (stashed by the accept page) if any.
  const goAfterAuth = () => {
    const pending = sessionStorage.getItem("pendingInviteToken");
    if (pending) {
      sessionStorage.removeItem("pendingInviteToken");
      navigate(`/accept-invite?token=${encodeURIComponent(pending)}`);
    } else {
      setAuthed(true);
    }
  };

  // Land once the signed-in user is in context: platform admins start on the
  // platform console (they usually belong to no org, so /dashboard would be empty).
  useEffect(() => {
    if (!authed || !user) return;
    navigate(user.permissions.includes("org:manage") ? "/platform" : "/dashboard");
  }, [authed, user]);

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const onPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await login(email.trim(), password);
      if (res.passwordExpired) {
        setChallenge(res.challengeToken ?? null);
        setStep("expired");
      } else if (res.mfaRequired) {
        setChallenge(res.challengeToken ?? null);
        setCode("");
        setStep("mfa");
      } else {
        goAfterAuth();
      }
    });
  };

  const onRegister = (e: FormEvent) => {
    e.preventDefault();
    // Same policy the API enforces — fail fast with the checklist instead of a roundtrip.
    if (!passwordMeetsPolicy(password)) {
      setPwdRejected(true);
      return;
    }
    void run(async () => {
      await register(email.trim(), password, name.trim() || undefined);
      // Sign-up never returns a session now: the API answers identically for a
      // free and an already-taken address so it cannot be used to test whether
      // an email is registered. There is therefore nothing to branch on — every
      // sign-up lands on the sign-in form with the same neutral notice. The
      // email stays filled in so continuing is one field away.
      setMode("login");
      setStep("credentials");
      setPassword("");
      setName("");
      setPwdRejected(false);
      setNotice(t("login.registerPending"));
    });
  };

  const submitMfa = (mfaCode: string) =>
    run(async () => {
      try {
        await verifyMfa(challenge!, mfaCode);
        goAfterAuth();
      } catch (err) {
        setCode(""); // clear the cells so the user can retype immediately
        throw err;
      }
    });

  const onMfa = (e: FormEvent) => {
    e.preventDefault();
    void submitMfa(code);
  };

  const onNewPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!passwordMeetsPolicy(newPassword)) {
      setPwdRejected(true);
      return;
    }
    void run(async () => {
      await completeExpiredPassword(challenge!, newPassword);
      backToCredentials();
      setPassword("");
      setNewPassword("");
      setNotice(t("login.passwordUpdated"));
    });
  };

  const backToCredentials = () => {
    setStep("credentials");
    setChallenge(null);
    setCode("");
    setError(null);
    setPwdRejected(false);
  };

  const toggleMode = () => {
    setMode(mode === "register" ? "login" : "register");
    setError(null);
    setNotice(null);
    setPwdRejected(false);
  };

  const heading =
    step === "mfa"
      ? { title: t("login.mfaTitle"), sub: t("login.mfaPrompt") }
      : step === "expired"
        ? { title: t("login.expiredTitle"), sub: t("login.passwordExpired") }
        : mode === "register"
          ? { title: t("login.registerTitle"), sub: t("login.registerSubtitle") }
          : { title: t("login.title"), sub: t("login.subtitle") };

  // The checklist already shows what's missing; the field error just draws the eye to it.
  const pwdError = (v: string) => (pwdRejected && !passwordMeetsPolicy(v) ? t("login.pwdWeak") : undefined);

  return (
    <div className="auth-layout">
      {/* Brand panel — pure presentation, hidden on narrow screens. */}
      <div className="auth-brand-panel" aria-hidden="true">
        <div className="auth-brand-logo">
          <BrandMark size={22} className="brand-mark" />
          IDPP<span>.net</span>
        </div>
        <div className="ld-scene auth-scene">
          <div className="ld-floor" />
          <i className="ld-orb o1" />
          <i className="ld-orb o2" />
          <PassportCard3D lang={lang} />
        </div>
        <ul className="auth-pitch">
          <li>{t("login.pitch1")}</li>
          <li>{t("login.pitch2")}</li>
          <li>{t("login.pitch3")}</li>
          <li>{t("login.pitch4")}</li>
        </ul>
        <div className="auth-standards">ESPR 2024/1781 · EN 1821x · GS1 DIGITAL LINK · JSON-LD</div>
      </div>

      {/* Form column. */}
      <div className="auth-form-panel">
        <div className="auth-controls-corner">
          <AuthControls />
        </div>
        <div className="auth-form-inner">
          <span className="auth-kicker">IDPP.NET</span>
          <Title level={2} className="auth-title">
            {heading.title}
          </Title>
          <Text secondary className="auth-subtitle">
            {heading.sub}
          </Text>

          {notice && <Alert type="success">{notice}</Alert>}
          {error != null && <ErrorAlert error={error} />}
          {step === "credentials" && pendingInvite && <Alert type="info">{t("login.inviteNotice")}</Alert>}

          {step === "expired" ? (
            <>
              <form onSubmit={onNewPassword}>
                <PasswordInput
                  label={t("login.newPassword")}
                  autoComplete="new-password"
                  prefix={<Icon name="lock" size={14} />}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={pwdError(newPassword)}
                  autoFocus
                  required
                />
                <PasswordChecklist value={newPassword} />
                <Button variant="primary" block loading={busy} type="submit">
                  {t("login.updatePassword")}
                </Button>
              </form>
              <div className="auth-back">
                <Button variant="link" onClick={backToCredentials}>
                  {t("login.back")}
                </Button>
              </div>
            </>
          ) : step === "mfa" ? (
            <>
              <form onSubmit={onMfa}>
                <OtpInput
                  label={t("login.mfaCode")}
                  value={code}
                  onInput={setCode}
                  onComplete={(c) => {
                    setCode(c);
                    void submitMfa(c);
                  }}
                  disabled={busy}
                  autoFocus
                />
                <Button variant="primary" block loading={busy} type="submit">
                  {t("login.verify")}
                </Button>
              </form>
              <div className="auth-back">
                <Button variant="link" onClick={backToCredentials}>
                  {t("login.back")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={mode === "register" ? onRegister : onPassword}>
                <Input
                  label={t("common.email")}
                  type="email"
                  autoComplete="email"
                  placeholder={t("login.emailPlaceholder")}
                  prefix={<Icon name="mail" size={14} />}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
                {mode === "register" && (
                  <Input
                    label={t("login.name")}
                    autoComplete="name"
                    prefix={<Icon name="user" size={14} />}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    help={t("login.nameHelp")}
                  />
                )}
                <PasswordInput
                  label={t("login.password")}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  prefix={<Icon name="lock" size={14} />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={mode === "register" ? pwdError(password) : undefined}
                  required
                />
                {mode === "register" && <PasswordChecklist value={password} />}
                {mode === "login" && (
                  <div className="auth-forgot">
                    <Button variant="link" onClick={() => navigate("/forgot-password")}>
                      {t("login.forgotPassword")}
                    </Button>
                  </div>
                )}
                <Button variant="primary" block loading={busy} type="submit">
                  {mode === "register" ? t("login.register") : t("login.continue")}
                </Button>
              </form>
              <div className="auth-switch">
                <Button variant="link" onClick={toggleMode}>
                  {mode === "register" ? t("login.haveAccount") : t("login.noAccount")}
                </Button>
              </div>
            </>
          )}

          <div className="auth-explore">
            <Link to="/">{t("login.explore")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
